import type {
  RunState,
  RoomData,
  GuessResult,
  RoomCompletionResult,
  HintWithMetadata,
  PowerUpItem,
  RoomModifier,
} from '@crackpoint/shared/types';
import type { StorageAdapter } from './StorageAdapter';
import {
  generateRoom,
  getRoomConfig,
  evaluateGuess,
  type Room as EngineRoom,
  type ScoredHint,
  PowerUpTier,
} from '../engine';
import { generateId } from '@crackpoint/shared/utils';

import type { GameAdapter } from './GameAdapter';

export class ClientAdapter implements GameAdapter {
  private runState: RunState | null = null;
  private roomData: RoomData | null = null;
  private engineRoom: EngineRoom | null = null;
  private usedReserveCount = 0;
  private usedPowerUpIndices = new Set<number>();
  private completionResult: RoomCompletionResult | null = null;
  private attemptCount = 0;
  private hiddenHintRevealed = false;
  private storage: StorageAdapter;

  constructor(storage: StorageAdapter) {
    this.storage = storage;
  }

  async createRun(): Promise<RunState> {
    this.runState = {
      id: generateId(),
      lives_remaining: 5,
      rooms_cleared: 0,
      current_streak: 0,
      highest_streak: 0,
      total_stars: 0,
      current_room_number: 0,
      inventory: [],
      state: 'active',
    };

    await this._saveCheckpoint();
    return this.runState;
  }

  async generateRoom(): Promise<RoomData> {
    if (!this.runState) {
      throw new Error('No active run state');
    }

    this.runState.current_room_number += 1;
    const roomNumber = this.runState.current_room_number;

    const config = getRoomConfig(roomNumber);
    this.engineRoom = generateRoom(roomNumber, config);

    this.roomData = {
      room_number: roomNumber,
      code_length: this.engineRoom.codeLength,
      base_hints: this.engineRoom.baseHints.map((h) => this._scoredHintToMetadata(h)),
      reserve_hint_count: this.engineRoom.reserveHints.length,
      modifier: this.engineRoom.modifier as RoomModifier,
      power_ups: this.engineRoom.roomPowerUps.map((h) => this._scoredHintToPowerUp(h)),
      hidden_hint_index: this.engineRoom.hiddenHintIndex,
    };

    this.usedReserveCount = 0;
    this.usedPowerUpIndices.clear();
    this.completionResult = null;
    this.attemptCount = 0;
    this.hiddenHintRevealed = false;

    await this._saveCheckpoint();
    return this.roomData;
  }

  async submitGuess(guess: number[]): Promise<GuessResult> {
    if (!this.engineRoom || !this.runState || !this.roomData) {
      throw new Error('No active room or run state');
    }

    const evaluation = evaluateGuess(this.engineRoom.solution, guess);
    this.attemptCount += 1;

    const solved = evaluation.correct_placed === this.engineRoom.solution.length;
    let hiddenHintRevealed: HintWithMetadata | null = null;

    if (!solved) {
      this.runState.lives_remaining -= 1;
      this.runState.current_streak = 0;

      if (
        this.roomData.modifier.id === 'dark' &&
        this.roomData.hidden_hint_index !== null &&
        !this.hiddenHintRevealed
      ) {
        const hiddenHint = this.engineRoom.baseHints[this.roomData.hidden_hint_index];
        hiddenHintRevealed = this._scoredHintToMetadata(hiddenHint);
        this.hiddenHintRevealed = true;
      }

      if (this.runState.lives_remaining <= 0) {
        this.runState.state = 'completed';
      }
    } else {
      const usedOnlyBaseHints = this.usedReserveCount === 0 && this.usedPowerUpIndices.size === 0;
      const isOptimal = this.attemptCount <= this.engineRoom.optimalSize;

      let stars = 1;
      if (usedOnlyBaseHints) {
        stars = isOptimal ? 3 : 2;
      }

      this.runState.rooms_cleared += 1;
      this.runState.total_stars += stars;
      this.runState.current_streak += 1;
      this.runState.highest_streak = Math.max(
        this.runState.highest_streak,
        this.runState.current_streak,
      );

      const nextConfig = getRoomConfig(this.runState.current_room_number + 1);
      const nextModifier = nextConfig.modifier as RoomModifier;

      const loot: PowerUpItem | null = this.engineRoom.loot
        ? {
            type: this._tierToDisplayType(this.engineRoom.loot.power_up as PowerUpTier),
            tier: this.engineRoom.loot.power_up,
            digits: [...this.engineRoom.loot.digits],
            correct_placed: this.engineRoom.loot.correct_placed,
            correct_misplaced: this.engineRoom.loot.correct_misplaced,
          }
        : null;

      if (loot) {
        this.runState.inventory.push(loot);
      }

      this.runState.lives_remaining += this.engineRoom.bonusLives;

      this.completionResult = {
        stars_earned: stars,
        optimal_hint_count: this.engineRoom.optimalSize,
        loot,
        bonus_lives: this.engineRoom.bonusLives,
        run_state: { ...this.runState },
        next_room_modifier: nextModifier,
      };
    }

    await this._saveCheckpoint();

    return {
      correct_placed: evaluation.correct_placed,
      correct_misplaced: evaluation.correct_misplaced,
      solved,
      lives_remaining: this.runState.lives_remaining,
      hidden_hint_revealed: hiddenHintRevealed,
      run_ended: this.runState.state === 'completed',
    };
  }

  async drawReserveHint(): Promise<HintWithMetadata | null> {
    if (!this.engineRoom) {
      throw new Error('No active room');
    }

    if (this.usedReserveCount >= this.engineRoom.reserveHints.length) {
      return null;
    }

    const hint = this.engineRoom.reserveHints[this.usedReserveCount];
    this.usedReserveCount += 1;

    await this._saveCheckpoint();
    return this._scoredHintToMetadata(hint);
  }

  async usePowerUp(index: number): Promise<PowerUpItem> {
    if (!this.engineRoom) {
      throw new Error('No active room');
    }

    if (index < 0 || index >= this.engineRoom.roomPowerUps.length) {
      throw new Error(`Invalid power-up index: ${index}`);
    }

    this.usedPowerUpIndices.add(index);
    const hint = this.engineRoom.roomPowerUps[index];

    await this._saveCheckpoint();
    return this._scoredHintToPowerUp(hint);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async completeRoom(): Promise<RoomCompletionResult> {
    if (!this.completionResult) {
      throw new Error('No completion result available');
    }

    return this.completionResult;
  }

  getRunState(): RunState | null {
    return this.runState ? { ...this.runState } : null;
  }

  getRoomData(): RoomData | null {
    return this.roomData ? { ...this.roomData } : null;
  }

  async loadCheckpoint(): Promise<void> {
    const data = await this.storage.get('crackpoint_checkpoint');
    if (!data) {
      return;
    }

    const checkpoint = JSON.parse(data) as {
      runState?: RunState;
      usedReserveCount?: number;
      usedPowerUpIndices?: number[];
      attemptCount?: number;
      hiddenHintRevealed?: boolean;
    };
    this.runState = checkpoint.runState ?? null;
    this.usedReserveCount = checkpoint.usedReserveCount ?? 0;
    this.usedPowerUpIndices = new Set(checkpoint.usedPowerUpIndices ?? []);
    this.attemptCount = checkpoint.attemptCount ?? 0;
    this.hiddenHintRevealed = checkpoint.hiddenHintRevealed ?? false;

    if (this.runState && this.runState.current_room_number > 0) {
      const config = getRoomConfig(this.runState.current_room_number);
      this.engineRoom = generateRoom(this.runState.current_room_number, config);
      this.roomData = {
        room_number: this.engineRoom.roomNumber,
        code_length: this.engineRoom.codeLength,
        base_hints: this.engineRoom.baseHints.map((h) => this._scoredHintToMetadata(h)),
        reserve_hint_count: this.engineRoom.reserveHints.length,
        modifier: this.engineRoom.modifier as RoomModifier,
        power_ups: this.engineRoom.roomPowerUps.map((h) => this._scoredHintToPowerUp(h)),
        hidden_hint_index: this.engineRoom.hiddenHintIndex,
      };
    }
  }

  private async _saveCheckpoint(): Promise<void> {
    if (!this.runState) {
      return;
    }

    const checkpoint = {
      runState: this.runState,
      usedReserveCount: this.usedReserveCount,
      usedPowerUpIndices: Array.from(this.usedPowerUpIndices),
      attemptCount: this.attemptCount,
      hiddenHintRevealed: this.hiddenHintRevealed,
    };

    await this.storage.set('crackpoint_checkpoint', JSON.stringify(checkpoint));
  }

  private _scoredHintToMetadata(hint: ScoredHint): HintWithMetadata {
    return {
      id: generateId(),
      digits: [...hint.hint.digits],
      correct_placed: hint.hint.correct_placed,
      correct_misplaced: hint.hint.correct_misplaced,
      rarity: hint.rarity,
      power_up: hint.power_up,
      percentile: hint.percentile,
      drawn_at: Date.now(),
      is_new: true,
    };
  }

  private _scoredHintToPowerUp(hint: ScoredHint): PowerUpItem {
    if (!hint.power_up) {
      throw new Error('Hint is not a power-up');
    }

    return {
      type: this._tierToDisplayType(hint.power_up),
      tier: hint.power_up,
      digits: [...hint.hint.digits],
      correct_placed: hint.hint.correct_placed,
      correct_misplaced: hint.hint.correct_misplaced,
    };
  }

  private _tierToDisplayType(tier: PowerUpTier): 'oracle' | 'surge' | 'boost' {
    switch (tier) {
      case PowerUpTier.ORACLE:
        return 'oracle';
      case PowerUpTier.SURGE_I:
      case PowerUpTier.SURGE_II:
        return 'surge';
      case PowerUpTier.BOOST:
        return 'boost';
    }
  }
}
